/**
 * 対象SKU CSV のファイル選択欄。
 *
 * 30万SKU（約11.1MB）を扱うため、全件の検証は画面では行わない。拡張子・サイズ・
 * 先頭 `TARGET_SKUS_SAMPLE_LINES` 行のサンプル検証だけを通し、全件の UUID 検証は
 * ジョブ実行時（Fargate）に任せる。UI の下敷きは `seller-tag/TagImportDialog.tsx`。
 */

import { useRef, useState } from 'react';
import { Ban, FileUp, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { formatByteSize } from '@/lib/custom-dashboard/upload-validation';
import {
  TARGET_SKUS_ALLOWED_EXTENSIONS,
  TARGET_SKUS_FILE_MAX_BYTES,
  TARGET_SKUS_SAMPLE_LINES,
} from '@/lib/api/ds/prediction-error-evaluation-upload';
import { MAX_REPORTED_INVALID_LINES, toCanonicalSkuUuid } from './parseSkuLines';
import { targetSkuFileExtension } from './targetSkuFileRules';
import type { InvalidSkuLine } from './parseSkuLines';

/** 隠し file input の aria-label。テストと実装で同じ文言を使う */
export const TARGET_SKU_FILE_INPUT_LABEL = '対象SKUのCSVファイルを選ぶ';

/** 画面に出す拡張子の一覧（`.csv / .txt`） */
const EXTENSION_LABEL = TARGET_SKUS_ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(' / ');

export interface TargetSkuFileInputProps {
  /** 選択中のファイル。未選択なら null */
  file: File | null;
  /**
   * 検査を通ったファイルを親へ渡す。取り消し時は null。
   * 検査で弾いたファイルは渡さない（親はアップロード可能なファイルだけを持つ）
   */
  onFileChange: (file: File | null) => void;
  /**
   * ファイルの選択・取り消しを一切させないとき true。
   *
   * テキスト入力側に値があるとき（対象SKUの指定手段は排他のため）と、
   * S3 へのアップロード中（送信処理が捕捉したファイルと表示が食い違うため）に立てる。
   */
  disabled?: boolean;
  /** disabled の理由。押せない理由を利用者に伝えるために受け取る */
  disabledReason?: string;
}

/** 選択済みファイルの要約。`file` で同一性を見て、親の state とずれた表示を残さない */
interface SelectedFileSummary {
  file: File;
  /** ヘッダ行を除いたデータ行の件数 */
  lineCount: number;
}

/** CSV 1 行から SKU の値を取り出した結果 */
interface CsvInspection {
  /** ヘッダ行を除いたデータ行の件数 */
  lineCount: number;
  /** サンプル範囲で UUID として読めなかった行（`MAX_REPORTED_INVALID_LINES` で打ち切る） */
  invalidLines: InvalidSkuLine[];
  /** 打ち切る前のサンプル範囲の不正行の総件数 */
  invalidLineTotal: number;
}

/**
 * UTF-8 BOM。Excel で保存した CSV は先頭に付く。
 *
 * `FileReader.readAsText` は Encoding 仕様に従って BOM を落とすため、この除去が実際に
 * 効く場面は通常ない（読み取り方法を変えたときのための防御として残している）。
 * 実効的な BOM 対策は BE 側（バイト列段階と文字列段階の2層）にある。
 */
const BOM = /^\uFEFF/;

/**
 * 行から SKU の値を取り出す。
 *
 * DBツールの CSV 出力は `"3fa85f64-..."` と引用符を付けたり `3fa85f64-...,商品名` と
 * 列を続けたりするため、先頭のカンマまでを取り出して前後の `"` を剥がす。
 *
 * BE の `parse_target_sku_csv`（`trim()` → `trim_matches('"')` → `trim()`）と同じ範囲を
 * 受理する必要がある。画面側が少しでも厳しいと、BE なら通る行を「読めない行」として
 * 弾いてしまい、利用者には原因が分からない。そのため連続する引用符をまとめて剥がし、
 * 引用符の内側の空白（`" uuid "`）も落とす。
 */
function extractFirstField(line: string): string {
  return line
    .split(',', 1)[0]
    .trim()
    .replace(/^"+|"+$/g, '')
    .trim();
}

/**
 * CSV を検査する。全件は検証せず、データ行の先頭 `TARGET_SKUS_SAMPLE_LINES` 行だけを見る。
 *
 * 1行目が UUID として解釈できない場合のみヘッダ行として読み飛ばす（DBツールからの
 * CSV 出力にはヘッダが付くのが普通で、これを弾くと実用的でないため）。
 * 行番号は物理行を維持する（ユーザーが該当行を探せるようにするため）。
 */
function inspectTargetSkuCsv(content: string): CsvInspection {
  // BOM は readAsText が落とすため通常は残らない。残った場合の防御（詳細は BOM の定義）
  const lines = content.replace(BOM, '').split('\n');
  const invalidLines: InvalidSkuLine[] = [];
  let invalidLineTotal = 0;
  let lineCount = 0;
  let headerChecked = false;

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index].trim();
    if (line === '') {
      continue;
    }

    if (!headerChecked) {
      headerChecked = true;
      if (toCanonicalSkuUuid(extractFirstField(line)) === null) {
        continue;
      }
    }

    lineCount += 1;
    // サンプル範囲より後ろは検証しない（30万行を検証すると UI が固まる）
    if (lineCount > TARGET_SKUS_SAMPLE_LINES) {
      continue;
    }
    if (toCanonicalSkuUuid(extractFirstField(line)) !== null) {
      continue;
    }

    invalidLineTotal += 1;
    if (invalidLines.length < MAX_REPORTED_INVALID_LINES) {
      invalidLines.push({ lineNumber: index + 1, value: line });
    }
  }

  return { lineCount, invalidLines, invalidLineTotal };
}

/** 拡張子とサイズを見て、受け付けられない理由を返す。問題なければ null */
function rejectReasonBeforeRead(file: File): string | null {
  const extension = targetSkuFileExtension(file.name);
  if (!TARGET_SKUS_ALLOWED_EXTENSIONS.some((allowed) => allowed === extension)) {
    return `対象SKUのファイルは ${EXTENSION_LABEL} のみ指定できます`;
  }
  if (file.size > TARGET_SKUS_FILE_MAX_BYTES) {
    return `ファイルサイズが上限の${formatByteSize(TARGET_SKUS_FILE_MAX_BYTES)}を超えています（${formatByteSize(file.size)}）`;
  }
  return null;
}

/** サンプル検証の結果から、受け付けられない理由を返す。問題なければ null */
function rejectReasonAfterRead(inspection: CsvInspection): string | null {
  if (inspection.lineCount === 0) {
    return '対象SKUが1行も読み取れませんでした。1行に1つのUUIDを書いたファイルを指定してください';
  }
  if (inspection.invalidLineTotal === 0) {
    return null;
  }

  const detail = inspection.invalidLines
    .map((line) => `${line.lineNumber}行目「${line.value}」`)
    .join(', ');
  const rest = inspection.invalidLineTotal - inspection.invalidLines.length;
  return `先頭${TARGET_SKUS_SAMPLE_LINES}行にUUIDとして読めない行が${inspection.invalidLineTotal}件あります: ${detail}${
    rest > 0 ? ` ほか${rest}件` : ''
  }`;
}

/** ファイルをテキストとして読む（jsdom に `Blob.text()` が無いため FileReader を使う） */
function readAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => resolve(String(reader.result ?? '')));
    reader.addEventListener('error', () => reject(new Error('ファイルを読み込めませんでした')));
    reader.readAsText(file);
  });
}

/**
 * 選択されたファイルの要約は `role="status"`、検査で弾いた理由は `role="alert"` で出す
 * （`TagImportDialog` と同じ作法。読み上げと単体テストの双方がこれに依存する）。
 */
export function TargetSkuFileInput({
  file,
  onFileChange,
  disabled = false,
  disabledReason,
}: TargetSkuFileInputProps): React.ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);
  const [summary, setSummary] = useState<SelectedFileSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const clearInputValue = () => {
    // 同じファイルを選び直したときにも change が飛ぶようにする
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const acceptFile = async (selected: File) => {
    const beforeRead = rejectReasonBeforeRead(selected);
    if (beforeRead !== null) {
      setSummary(null);
      setError(beforeRead);
      clearInputValue();
      return;
    }

    let inspection: CsvInspection;
    try {
      inspection = inspectTargetSkuCsv(await readAsText(selected));
    } catch (e) {
      setSummary(null);
      setError(e instanceof Error ? e.message : 'ファイルを読み込めませんでした');
      clearInputValue();
      return;
    }

    const afterRead = rejectReasonAfterRead(inspection);
    if (afterRead !== null) {
      setSummary(null);
      setError(afterRead);
      clearInputValue();
      return;
    }

    setSummary({ file: selected, lineCount: inspection.lineCount });
    setError(null);
    onFileChange(selected);
  };

  const handleSelect = (selected: File | undefined) => {
    if (disabled || !selected) {
      return;
    }
    void acceptFile(selected);
  };

  const handleClear = () => {
    // ボタンは disabled だが、取り消しが通ると送信中のファイルと表示が食い違うため二重に塞ぐ
    if (disabled) {
      return;
    }
    setSummary(null);
    setError(null);
    clearInputValue();
    onFileChange(null);
  };

  // 親の state とずれた要約を残さない（親が受け取らなかったファイルは表示しない）
  const selectedSummary = file !== null && summary?.file === file ? summary : null;

  return (
    <div className="space-y-2">
      <Label htmlFor="target-skus-file">対象SKUのCSVファイル（任意）</Label>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) {
            setDragging(true);
          }
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          handleSelect(e.dataTransfer.files?.[0]);
        }}
        className={`flex flex-col items-center gap-2 rounded-md border-2 border-dashed px-4 py-5 text-center transition ${
          dragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
        } ${disabled ? 'opacity-60' : ''}`}
      >
        <FileUp className="h-5 w-5 text-gray-400" />
        <div className="text-xs text-gray-600">
          CSVファイルをここにドロップ、または
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto px-1 py-0 text-xs"
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
          >
            ファイルを選ぶ
          </Button>
        </div>
        <span className="text-[11px] text-gray-500">
          {EXTENSION_LABEL} に対応（上限{formatByteSize(TARGET_SKUS_FILE_MAX_BYTES)}）。1行に1つの
          UUIDを書いてください
        </span>
        <span className="text-[11px] text-gray-500">
          画面ではデータ行の先頭{TARGET_SKUS_SAMPLE_LINES}
          行だけを確認します。全件の検証はジョブ実行時に行います
        </span>
        <input
          id="target-skus-file"
          ref={inputRef}
          type="file"
          accept=".csv,.txt,text/csv,text/plain"
          className="hidden"
          aria-label={TARGET_SKU_FILE_INPUT_LABEL}
          disabled={disabled}
          onChange={(e) => handleSelect(e.target.files?.[0])}
        />
      </div>

      {disabled && disabledReason && (
        <p className="text-xs text-muted-foreground">{disabledReason}</p>
      )}

      {selectedSummary && (
        <div
          role="status"
          className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800"
        >
          <span className="flex-1 truncate">
            <b>{selectedSummary.file.name}</b>（{formatByteSize(selectedSummary.file.size)} /{' '}
            {selectedSummary.lineCount.toLocaleString('ja-JP')}行）
          </span>
          {/* アップロード中（disabled）に取り消せると、送信処理が捕捉したファイルで
              ジョブが起動したまま画面だけ未選択に戻ってしまう */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-2"
            disabled={disabled}
            onClick={handleClear}
          >
            <X className="mr-1 h-3 w-3" />
            取り消す
          </Button>
        </div>
      )}

      {error && (
        <p
          role="alert"
          className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
        >
          <Ban className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{error}</span>
        </p>
      )}
    </div>
  );
}
